/**
 * PostMatchStats — DOM table renderer for the post-match scoreboard.
 *
 * Server emits a `stats` array on the `MatchWon` event. This module turns
 * that array into an HTMLElement: one row per player, sorted by placement,
 * with the winner row highlighted. The element is intentionally
 * detached so HudManager can append it inside the existing match-end overlay.
 */

import { formatMMSS } from './format.js';

export interface PostMatchPlayerStats {
  sessionId: string;
  username: string;
  placement: number;
  score: number;
  kills: number;
  damageDealt: number;
  damageTaken: number;
  longestStreak: number;
  timeAliveMs: number;
}

const COLUMNS: ReadonlyArray<{ label: string; key: keyof PostMatchPlayerStats | 'kd' | 'time' }> = [
  { label: 'RANK', key: 'placement' },
  { label: 'PLAYER', key: 'username' },
  { label: 'K/D', key: 'kd' },
  { label: 'SCORE', key: 'score' },
  { label: 'DMG OUT', key: 'damageDealt' },
  { label: 'DMG IN', key: 'damageTaken' },
  { label: 'STREAK', key: 'longestStreak' },
  { label: 'ALIVE', key: 'time' },
];

export function buildPostMatchStatsTable(
  entries: ReadonlyArray<PostMatchPlayerStats>,
  localUsername: string,
): HTMLElement {
  const sorted = [...entries].sort((a, b) => a.placement - b.placement);

  const table = document.createElement('div');
  table.className = 'post-match-stats';

  const header = document.createElement('div');
  header.className = 'post-match-stats-row header';
  header.style.gridTemplateColumns = gridTemplate();
  for (const col of COLUMNS) {
    const cell = document.createElement('span');
    cell.textContent = col.label;
    header.appendChild(cell);
  }
  table.appendChild(header);

  for (const entry of sorted) {
    const row = document.createElement('div');
    row.className = 'post-match-stats-row';
    row.style.gridTemplateColumns = gridTemplate();
    if (entry.placement === 1) row.classList.add('winner');
    if (entry.username === localUsername) row.classList.add('self');

    const rankCell = document.createElement('span');
    rankCell.className = 'rank';
    rankCell.textContent = `#${entry.placement}`;
    row.appendChild(rankCell);

    const nameCell = document.createElement('span');
    nameCell.className = 'name';
    nameCell.textContent = entry.username;
    row.appendChild(nameCell);

    appendNumberCell(row, `${entry.kills}/${kdDeaths(entry)}`);
    appendNumberCell(row, String(entry.score));
    appendNumberCell(row, String(entry.damageDealt));
    appendNumberCell(row, String(entry.damageTaken));
    appendNumberCell(row, String(entry.longestStreak));
    appendNumberCell(row, formatMMSS(entry.timeAliveMs / 1000));

    table.appendChild(row);
  }

  return table;
}

function appendNumberCell(row: HTMLElement, text: string): void {
  const cell = document.createElement('span');
  cell.className = 'num';
  cell.textContent = text;
  row.appendChild(cell);
}

/**
 * Deaths are not tracked server-side (only kills/score), so the D in K/D is
 * rendered as a dash to avoid implying a real value.
 */
function kdDeaths(_entry: PostMatchPlayerStats): string {
  return '-';
}

function gridTemplate(): string {
  return '50px 1fr 60px 60px 70px 70px 60px 60px';
}
